<script setup>
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Card from 'primevue/card';

const props = defineProps({
    banner: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['dismiss', 'click']);

const isDismissed = ref(false);

const hasBanner = computed(() => !!props.banner && !isDismissed.value);

const bannerStyles = computed(() => {
    if (!props.banner) return {};

    const styles = {};
    if (props.banner.background_color) {
        styles.backgroundColor = props.banner.background_color;
    }
    if (props.banner.text_color) {
        styles.color = props.banner.text_color;
    }
    return styles;
});

const isImageBg = computed(() => props.banner?.layout === 'image-bg');
const isImageLeft = computed(() => props.banner?.layout === 'image-left');
const isImageRight = computed(() => props.banner?.layout === 'image-right');

function onClickCta() {
    if (!props.banner?.cta_url) return;
    emit('click', props.banner);
    window.open(props.banner.cta_url, '_blank');
}

function onDismiss() {
    if (!props.banner) return;
    isDismissed.value = true;
    emit('dismiss', props.banner);

    // Store dismissal in localStorage
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const dismissKey = `overview:banner:dismissed:${props.banner.id}:${today}`;
    localStorage.setItem(dismissKey, '1');
}

// Check if banner was dismissed today
if (props.banner) {
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const dismissKey = `overview:banner:dismissed:${props.banner.id}:${today}`;
    if (localStorage.getItem(dismissKey)) {
        isDismissed.value = true;
    }
}
</script>

<template>
    <div v-if="hasBanner" class="hero-banner-wrapper mb-4">
        <!-- Image Background Layout -->
        <Card v-if="isImageBg" class="hero-banner overflow-hidden" :style="bannerStyles" role="banner" :aria-label="banner?.title">
            <template #content>
                <div class="relative">
                    <img
                        :src="banner.image_url"
                        :alt="banner.alt_text || banner.title"
                        class="w-full h-80 object-cover"
                        loading="lazy"
                    />
                    <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
                        <div class="absolute bottom-0 left-0 right-0 p-6 text-white">
                            <h2 class="text-3xl font-bold mb-2">{{ banner.title }}</h2>
                            <p class="text-lg mb-4 opacity-95" :class="{ 'invisible': !banner.subtitle }">
                                {{ banner.subtitle || '&nbsp;' }}
                            </p>
                            <div class="flex gap-2">
                                <Button
                                    v-if="banner.cta_label && banner.cta_url"
                                    :label="banner.cta_label"
                                    severity="primary"
                                    @click="onClickCta"
                                />
                                <Button
                                    v-if="banner.dismissible"
                                    icon="pi pi-times"
                                    severity="secondary"
                                    text
                                    rounded
                                    aria-label="Dismiss"
                                    @click="onDismiss"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Side Layout (Image Left/Right) -->
        <Card v-else class="hero-banner overflow-hidden" :style="bannerStyles" role="banner" :aria-label="banner?.title">
            <template #content>
                <div class="grid">
                    <!-- Image on left -->
                    <div v-if="isImageLeft" class="col-12 md:col-5">
                        <img
                            :src="banner.image_url"
                            :alt="banner.alt_text || banner.title"
                            class="w-full border-round"
                            loading="lazy"
                        />
                    </div>

                    <!-- Content -->
                    <div class="col-12" :class="{
                        'md:col-7': isImageLeft || isImageRight,
                        'md:col-12': !isImageLeft && !isImageRight
                    }">
                        <div class="p-3">
                            <h2 class="text-3xl font-bold mb-2">{{ banner.title }}</h2>
                            <p class="text-lg mb-4 text-color-secondary" :class="{ 'invisible': !banner.subtitle }">
                                {{ banner.subtitle || '&nbsp;' }}
                            </p>
                            <div class="flex gap-2 align-items-center">
                                <Button
                                    v-if="banner.cta_label && banner.cta_url"
                                    :label="banner.cta_label"
                                    severity="primary"
                                    @click="onClickCta"
                                />
                                <Button
                                    v-if="banner.dismissible"
                                    icon="pi pi-times"
                                    severity="secondary"
                                    text
                                    rounded
                                    aria-label="Dismiss"
                                    @click="onDismiss"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Image on right -->
                    <div v-if="isImageRight" class="col-12 md:col-5">
                        <img
                            :src="banner.image_url"
                            :alt="banner.alt_text || banner.title"
                            class="w-full border-round"
                            loading="lazy"
                        />
                    </div>
                </div>
            </template>
        </Card>
    </div>
</template>

<style scoped>
.hero-banner-wrapper {
    animation: fadeIn 0.5s ease-in;
}

.hero-banner {
    transition: all 0.3s ease;
}

.hero-banner:deep(.p-card-body) {
    padding: 0 !important;
}

.hero-banner:deep(.p-card-content) {
    padding: 0 !important;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .hero-banner h2 {
        font-size: 1.5rem;
    }

    .hero-banner p {
        font-size: 0.9rem;
    }
}
</style>
